import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationService, SocketEventNamespace, SocketEventType } from '@core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IncidentsFilterService, IncidentsService } from '@services';
import { NotificationsService } from '@ui';
import { catchError, concatMap, map, of, withLatestFrom } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { AuthSelectors } from '../auth';
import { ClientManagementActions } from '../clients-managements';
import { SocketEvents } from '../sockets';
import { IncidentsActions } from './incidents.actions';

@Injectable()
export class IncidentsEffects {
  private actions$ = inject(Actions);
  private incidentService = inject(IncidentsService);
  private notificationService = inject(NotificationsService);
  private store = inject(Store);
  private navigationService = inject(NavigationService);
  private incidentFilterService = inject(IncidentsFilterService);

  constructor() {
    SocketEvents
      .pipe(
        filter(({ type }) => type.startsWith(SocketEventNamespace.INCIDENTS)),
        takeUntilDestroyed(),
      )
      .subscribe(({ data, type }) => {
        switch (type) {
          case SocketEventType.INCIDENT_CREATED:
          case SocketEventType.INCIDENT_IN_PROGRESS:
            this.store.dispatch(IncidentsActions.addIncident({ incident: data }));
            break;
          case SocketEventType.INCIDENT_RESOLVED:
            // TODO : Need refactoring : different data : Naberezhniev
            this.store.dispatch(IncidentsActions.resolveIncidents({ incidents: data.data ?? data }));
            break;
        }
        // TODO : Need refactoring : different data : Naberezhniev
        const locId = this.navigationService.route.snapshot.params['locationId'];

        (Array.isArray(data) ? data : [data])
          .forEach(item => {
            const locationId = item?.device?.locationId || item?.data?.[0]?.device?.locationId || '';

            if (locationId === locId) {
              this.store.dispatch(IncidentsActions.loadAllIncidents());
              this.incidentFilterService.resolvedIncidentsRequest(true);
            }
          });
      });
  }

  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      switchMap(() => [
        IncidentsActions.resetIncidents(),
        IncidentsActions.loadAllIncidents(),
      ]),
    );
  });

  loadAllIncidents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncidentsActions.loadAllIncidents),
      concatMap(() =>
        this.incidentService.getAllIncidents().pipe(
          map(incidents => IncidentsActions.loadAllIncidentSuccess({ incidents })),
          catchError(error => of(IncidentsActions.loadAllIncidentError({ error }))),
        ),
      ),
    ),
  );

  locationErrorHandler = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IncidentsActions.loadAllIncidentError),
        withLatestFrom(this.store.select(AuthSelectors.isLoggedIn)),
        tap(([action, isLoggedIn]) => {
          if (isLoggedIn && action?.error?.message) {
            this.notificationService.showErrorMessage(action.error.message);
          }
        }),
      ),
    { dispatch: false },
  );
}
